/**
 * Mentor Coach Qualification (MCQ) training API client.
 *
 * Backed by /api/mentor-training routes.
 */
import { apiRequest } from './apiClient';
import type {
  AttemptResponse,
  AttemptSubmitRequest,
  DrillCreateRequest,
  DrillDetail,
  DrillSummary,
  InstructorAttemptRow,
} from '../../types/mentorTraining';

const BASE = '/mentor-training';

export const MentorTrainingAPI = {
  // ── Student endpoints ──────────────────────────────────────────────
  listMyDrills(): Promise<DrillSummary[]> {
    return apiRequest<DrillSummary[]>({ endpoint: `${BASE}/me/drills`, method: 'GET' });
  },
  getMyDrill(drillId: number): Promise<DrillDetail> {
    return apiRequest<DrillDetail>({
      endpoint: `${BASE}/me/drills/${drillId}`,
      method: 'GET',
    });
  },
  startMyDrill(drillId: number): Promise<AttemptResponse> {
    return apiRequest<AttemptResponse>({
      endpoint: `${BASE}/me/drills/${drillId}/start`,
      method: 'POST',
    });
  },
  submitMyDrill(drillId: number, payload: AttemptSubmitRequest): Promise<AttemptResponse> {
    return apiRequest<AttemptResponse, AttemptSubmitRequest>({
      endpoint: `${BASE}/me/drills/${drillId}/submit`,
      method: 'POST',
      data: payload,
    });
  },
  getMyAttempt(drillId: number): Promise<AttemptResponse | null> {
    return apiRequest<AttemptResponse>({
      endpoint: `${BASE}/me/drills/${drillId}/attempt`,
      method: 'GET',
    }).catch((err: any) => {
      const msg = String(err?.message || err);
      if (/404|not found|no attempt/i.test(msg)) return null;
      throw err;
    });
  },

  // ── Instructor endpoints ───────────────────────────────────────────
  createDrill(payload: DrillCreateRequest): Promise<DrillSummary> {
    return apiRequest<DrillSummary, DrillCreateRequest>({
      endpoint: `${BASE}/instructor/drills`,
      method: 'POST',
      data: payload,
    });
  },
  listCohortDrills(cohortId: number, includeArchived = false): Promise<DrillSummary[]> {
    return apiRequest<DrillSummary[]>({
      endpoint: `${BASE}/instructor/cohorts/${cohortId}/drills`,
      method: 'GET',
      params: { include_archived: includeArchived },
    });
  },
  getInstructorDrill(drillId: number): Promise<DrillDetail> {
    return apiRequest<DrillDetail>({
      endpoint: `${BASE}/instructor/drills/${drillId}`,
      method: 'GET',
    });
  },
  listDrillAttempts(drillId: number): Promise<InstructorAttemptRow[]> {
    return apiRequest<InstructorAttemptRow[]>({
      endpoint: `${BASE}/instructor/drills/${drillId}/attempts`,
      method: 'GET',
    });
  },
  getAttemptDetail(drillId: number, attemptId: number): Promise<AttemptResponse> {
    return apiRequest<AttemptResponse>({
      endpoint: `${BASE}/instructor/drills/${drillId}/attempts/${attemptId}`,
      method: 'GET',
    });
  },
  archiveDrill(drillId: number): Promise<DrillSummary> {
    return apiRequest<DrillSummary>({
      endpoint: `${BASE}/instructor/drills/${drillId}/archive`,
      method: 'POST',
    });
  },
};

export default MentorTrainingAPI;
